/**
 * Reconcile a pending proposal against the user's CURRENT inventory
 * (collected + duplicates) and return the patch to apply, or null if
 * nothing changed.
 *
 * Called by useAlbum.completeTrade after a trade lands: any pending
 * proposal whose receive/give lists still mention stickers we no
 * longer have (or already received) needs to be trimmed so the other
 * side doesn't keep expecting impossible exchanges.
 *
 * Inputs:
 *   prop            — proposal document data
 *                     { fromUid, toUid, receive, give, ... }
 *   userUid         — auth.uid of the user who just completed a trade
 *   collected       — user's new collected map after the completed trade
 *   duplicates      — user's new duplicates map after the completed trade
 *
 * Output:
 *   null  if nothing changed.
 *   {
 *     updateObj: { ... fields to merge into the proposal doc },
 *     removedFromMyReceive: ['MEX 5', ...]  // stickers user no longer needs
 *     removedFromMyGive:    ['ARG 7', ...]  // stickers user no longer has copies of
 *   }
 *
 * The updateObj carries the new receive/give lists AND a human-readable
 * systemMessage that ENUMERATES which stickers were withdrawn, so the
 * other party can see exactly what changed instead of a vague warning.
 */
export function reconcileProposalAfterTrade(prop, userUid, collected, duplicates) {
  if (!prop || !userUid) return null;

  const isSender = prop.fromUid === userUid;
  const myReceiveList = isSender ? (prop.receive || []) : (prop.give || []);
  const myGiveList = isSender ? (prop.give || []) : (prop.receive || []);

  // Lo que me sigue haciendo falta = lo que YO me esperaba recibir y
  // todavía no tengo coleccionado.
  const newMyReceiveList = myReceiveList.filter(s => !collected[s]);
  // Lo que aún puedo entregar = lo que YO ofrecí y todavía tengo
  // duplicado.
  const newMyGiveList = myGiveList.filter(s => (duplicates[s] || 0) > 0);

  const noChange =
    newMyReceiveList.length === myReceiveList.length &&
    newMyGiveList.length === myGiveList.length;
  if (noChange) return null;

  const removedFromMyReceive = myReceiveList.filter(s => !newMyReceiveList.includes(s));
  const removedFromMyGive = myGiveList.filter(s => !newMyGiveList.includes(s));

  // Mensaje NEUTRAL (sin perspectiva): ambas partes leen el mismo doc,
  // así que decir "ya recibidas" / "ya entregadas" desde el lado del
  // que completó confundía al otro lado ("¿yo cuándo las recibí?").
  // Enumeramos las láminas que CAYERON del trato en una sola lista,
  // dejando claro que es porque uno de los dos ya las intercambió
  // en otro lado.
  //
  // Además guardamos las listas crudas en `removedReceive` /
  // `removedGive` del doc para que la UI las pueda renderizar como
  // chips separadas si quiere mostrarlas.
  const allRemoved = [...removedFromMyReceive, ...removedFromMyGive];
  const detail = allRemoved.join(', ');

  const becameEmpty = newMyReceiveList.length === 0 && newMyGiveList.length === 0;

  const updateObj = {
    modifiedBySystem: true,
    systemMessage: becameEmpty
      ? `🚫 Propuesta cancelada automáticamente — todas las láminas (${detail}) ya se intercambiaron en otros tratos.`
      : `⚠️ Estas láminas se retiraron del trato porque uno de los dos ya las intercambió en otro lado: ${detail}.`,
    removedReceive: removedFromMyReceive,
    removedGive: removedFromMyGive,
  };

  if (isSender) {
    updateObj.receive = newMyReceiveList;
    updateObj.give = newMyGiveList;
  } else {
    // En esta dirección el sender's `give` = mi receive, así que las
    // listas se cruzan al escribir.
    updateObj.give = newMyReceiveList;
    updateObj.receive = newMyGiveList;
  }

  if (becameEmpty) updateObj.status = 'cancelled';

  return { updateObj, removedFromMyReceive, removedFromMyGive };
}
